import { motion } from "framer-motion";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  MessageCircle,
  Instagram,
  Facebook,
  Globe,
  Clock,
  MoreHorizontal,
  Phone,
  Eye,
} from "lucide-react";
import { cn } from "@/utils/cn";

export type TicketCard = {
  id: string;
  clientName: string;
  clientAvatar: string;
  lastMessage: string;
  agentName: string;
  channel: "whatsapp" | "instagram" | "facebook" | "widget";
  priority: "high" | "medium" | "low";
  slaTime: string;
  slaWarning: boolean;
  isOnline: boolean;
  tags: string[];
  columnId: string;
  timestamp: string;
};

const channelIcons = {
  whatsapp: { icon: MessageCircle, color: "#22C55E" },
  instagram: { icon: Instagram, color: "#E4405F" },
  facebook: { icon: Facebook, color: "#1877F2" },
  widget: { icon: Globe, color: "#2563EB" },
};

const priorityStyles = {
  high: { border: "#EF4444", bg: "bg-red-50", text: "text-red-600", label: "Alta" },
  medium: { border: "#F59E0B", bg: "bg-amber-50", text: "text-amber-600", label: "Média" },
  low: { border: "#22C55E", bg: "bg-green-50", text: "text-green-600", label: "Baixa" },
};

const tagColors: Record<string, { bg: string; text: string }> = {
  VIP: { bg: "bg-purple-100", text: "text-purple-700" },
  Cliente: { bg: "bg-blue-100", text: "text-blue-700" },
  Lead: { bg: "bg-emerald-100", text: "text-emerald-700" },
  Urgente: { bg: "bg-red-100", text: "text-red-700" },
  Demo: { bg: "bg-cyan-100", text: "text-cyan-700" },
  Fechado: { bg: "bg-green-100", text: "text-green-700" },
};

interface KanbanCardProps {
  ticket: TicketCard;
  isDragging?: boolean;
}

export function KanbanCard({ ticket, isDragging = false }: KanbanCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: ticket.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    borderLeftColor: priorityStyles[ticket.priority].border,
  };

  const ChannelIcon = channelIcons[ticket.channel].icon;

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      whileHover={{ y: -2, boxShadow: "0 8px 25px -5px rgba(0,0,0,0.1)" }}
      className={cn(
        "group cursor-grab rounded-[14px] border-l-4 bg-white p-4 shadow-sm border border-[#E2E8F0] transition-all duration-200",
        (isDragging || isSortableDragging) && "opacity-50 shadow-lg scale-105",
        "hover:border-[#CBD5E1]"
      )}
    >
      {/* Header Row */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div className="relative flex-shrink-0">
            <img
              src={ticket.clientAvatar}
              alt={ticket.clientName}
              className="h-10 w-10 rounded-full object-cover"
            />
            {ticket.isOnline && (
              <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-[#22C55E]" />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-semibold text-[#0F172A] truncate">
                {ticket.clientName}
              </p>
              <ChannelIcon
                className="h-3.5 w-3.5 flex-shrink-0"
                style={{ color: channelIcons[ticket.channel].color }}
              />
            </div>
            <p className="text-xs text-[#64748B]">{ticket.timestamp}</p>
          </div>
        </div>

        {/* Quick Actions - Visible on Hover */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button className="rounded-lg p-1.5 text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A]">
            <Phone className="h-3.5 w-3.5" />
          </button>
          <button className="rounded-lg p-1.5 text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A]">
            <Eye className="h-3.5 w-3.5" />
          </button>
          <button className="rounded-lg p-1.5 text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A]">
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Message Preview */}
      <p className="mt-3 text-sm text-[#64748B] line-clamp-2 leading-relaxed">
        {ticket.lastMessage}
      </p>

      {/* Tags */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {ticket.tags.map((tag) => (
          <span
            key={tag}
            className={cn(
              "rounded-full px-2 py-0.5 text-xs font-medium",
              tagColors[tag]?.bg || "bg-gray-100",
              tagColors[tag]?.text || "text-gray-700"
            )}
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-3 flex items-center justify-between border-t border-[#F1F5F9] pt-3">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-xs font-medium",
              priorityStyles[ticket.priority].bg,
              priorityStyles[ticket.priority].text
            )}
          >
            {priorityStyles[ticket.priority].label}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* SLA Timer */}
          {ticket.slaTime !== "-" && (
            <div
              className={cn(
                "flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium",
                ticket.slaWarning
                  ? "bg-red-50 text-red-600"
                  : "bg-[#F8FAFC] text-[#64748B]"
              )}
            >
              <Clock
                className={cn(
                  "h-3 w-3",
                  ticket.slaWarning && "animate-pulse"
                )}
              />
              <span>{ticket.slaTime}</span>
            </div>
          )}

          {/* Agent */}
          <div className="text-xs text-[#64748B]">
            <span className="hidden sm:inline">Agente: </span>
            <span className="font-medium text-[#0F172A]">
              {ticket.agentName.split(" ")[0]}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}